import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { BookOpen, Plus, Search, Edit2, Trash2, Tag, ToggleLeft, ToggleRight, X, Save, ChevronDown, ChevronUp } from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const CATEGORY_COLORS = {
  Booking: 'bg-blue-100 text-blue-800',
  Pricing: 'bg-emerald-100 text-emerald-800',
  Payment: 'bg-amber-100 text-amber-800',
  Venues: 'bg-purple-100 text-purple-800',
  Sports: 'bg-orange-100 text-orange-800',
  Account: 'bg-cyan-100 text-cyan-800',
  Tournaments: 'bg-rose-100 text-rose-800',
  WhatsApp: 'bg-green-100 text-green-800',
  Photos: 'bg-pink-100 text-pink-800',
  General: 'bg-slate-100 text-slate-700',
};

function KBEntryCard({ entry, onEdit, onDelete, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const colorClass = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.General;

  return (
    <Card data-testid={`kb-entry-${entry.id}`} className={`p-4 transition-all duration-200 ${!entry.is_active ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className={`${colorClass} text-xs font-medium`}>{entry.category}</Badge>
            {!entry.is_active && <Badge variant="outline" className="text-xs text-red-500 border-red-300">Inactive</Badge>}
          </div>
          <h3 className="font-semibold text-slate-900 text-sm leading-snug">{entry.question}</h3>
          {entry.image_url && (
            <div className="mt-2 mb-1">
              <img src={entry.image_url} alt={entry.question} className="w-32 h-20 object-cover rounded border border-slate-200" />
              <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-xs">{entry.image_url}</p>
            </div>
          )}
          <p className={`text-slate-600 text-sm mt-1 ${expanded ? '' : 'line-clamp-2'}`}>{entry.answer}</p>
          {entry.answer.length > 120 && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary mt-1 flex items-center gap-1 hover:underline">
              {expanded ? <><ChevronUp className="w-3 h-3" /> Less</> : <><ChevronDown className="w-3 h-3" /> More</>}
            </button>
          )}
          {entry.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {entry.keywords.map((kw, i) => (
                <span key={i} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{kw}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button variant="ghost" size="sm" data-testid={`kb-edit-${entry.id}`} onClick={() => onEdit(entry)} className="h-8 w-8 p-0">
            <Edit2 className="w-3.5 h-3.5 text-slate-500" />
          </Button>
          <Button variant="ghost" size="sm" data-testid={`kb-toggle-${entry.id}`} onClick={() => onToggle(entry)} className="h-8 w-8 p-0">
            {entry.is_active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}
          </Button>
          <Button variant="ghost" size="sm" data-testid={`kb-delete-${entry.id}`} onClick={() => onDelete(entry)} className="h-8 w-8 p-0">
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function KBForm({ entry, onSave, onCancel, categories }) {
  const [form, setForm] = useState({
    question: entry?.question || '',
    answer: entry?.answer || '',
    category: entry?.category || 'General',
    keywords: entry?.keywords?.join(', ') || '',
    image_url: entry?.image_url || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    if (form.category === 'Photos' && !form.image_url.trim()) {
      toast.error('Image URL is required for Photos category');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords.split(',').map(k => k.trim()).filter(Boolean),
      };
      if (form.category !== 'Photos') delete payload.image_url;
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="kb-form" className="space-y-4">
      <div>
        <Label>Category</Label>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger data-testid="kb-form-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[...new Set([...categories, 'Booking', 'Pricing', 'Payment', 'Venues', 'Sports', 'Account', 'Tournaments', 'WhatsApp', 'Photos', 'General'])].sort().map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {form.category === 'Photos' && (
        <div>
          <Label>Image URL *</Label>
          <Input data-testid="kb-form-image-url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://... (direct image URL)" />
          {form.image_url && (
            <img src={form.image_url} alt="Preview" className="mt-2 w-40 h-24 object-cover rounded border" />
          )}
        </div>
      )}
      <div>
        <Label>Question *</Label>
        <Input data-testid="kb-form-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="e.g. How do I book a court?" />
      </div>
      <div>
        <Label>Answer *</Label>
        <Textarea data-testid="kb-form-answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Detailed answer..." rows={4} />
      </div>
      <div>
        <Label>Keywords (comma-separated)</Label>
        <Input data-testid="kb-form-keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="book, reserve, court booking" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="submit" data-testid="kb-form-save" disabled={saving} className="flex-1">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : (entry ? 'Update Entry' : 'Add Entry')}
        </Button>
        <Button type="button" variant="outline" data-testid="kb-form-cancel" onClick={onCancel}>
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </div>
    </form>
  );
}

export default function KnowledgeBasePage() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchResults, setSearchResults] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchEntries = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
      if (searchTerm) params.search = searchTerm;
      const res = await axios.get(`${API}/knowledge-base`, { params, headers });
      setEntries(res.data.entries || []);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, searchTerm, token]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSave = async (data) => {
    try {
      if (editingEntry) {
        await axios.put(`${API}/knowledge-base/${editingEntry.id}`, data, { headers });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/knowledge-base`, data, { headers });
        toast.success('Entry added');
      }
      setShowForm(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.question.substring(0, 50)}..."?`)) return;
    try {
      await axios.delete(`${API}/knowledge-base/${entry.id}`, { headers });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  const handleToggle = async (entry) => {
    try {
      await axios.put(`${API}/knowledge-base/${entry.id}`, { is_active: !entry.is_active }, { headers });
      toast.success(entry.is_active ? 'Entry deactivated' : 'Entry activated');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to toggle entry');
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) { setSearchResults(null); return; }
    try {
      const res = await axios.post(`${API}/knowledge-base/search`, { query: searchTerm }, { headers });
      setSearchResults(res.data.results || []);
    } catch (err) {
      toast.error('Search failed');
    }
  };

  const activeCount = entries.filter(e => e.is_active).length;

  return (
    <DashboardLayout>
      <div data-testid="knowledge-base-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-primary" /> Knowledge Base
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {activeCount} active entries across {categories.length} categories — Powers WhatsApp AI responses
            </p>
          </div>
          <Button data-testid="kb-add-button" onClick={() => { setEditingEntry(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Add Entry
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <Card className="p-6 mb-6 border-2 border-primary/20">
            <h2 className="text-lg font-semibold mb-4">{editingEntry ? 'Edit Entry' : 'New Knowledge Base Entry'}</h2>
            <KBForm entry={editingEntry} onSave={handleSave} onCancel={() => { setShowForm(false); setEditingEntry(null); }} categories={categories} />
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="kb-search-input"
              className="pl-9"
              placeholder="Search questions, answers, or keywords..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setSearchResults(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v)}>
            <SelectTrigger data-testid="kb-filter-category" className="w-44">
              <Tag className="w-4 h-4 mr-1 text-slate-400" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="kb-semantic-search-btn" onClick={handleSearch} disabled={!searchTerm.trim()}>
            <Search className="w-4 h-4 mr-1" /> AI Search
          </Button>
        </div>

        {/* Semantic Search Results */}
        {searchResults && (
          <Card className="p-4 mb-5 border-purple-200 bg-purple-50/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-purple-900">AI Search Results ({searchResults.length} matches)</h3>
              <Button variant="ghost" size="sm" onClick={() => setSearchResults(null)} className="text-purple-600 h-7">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>
            {searchResults.length === 0 ? (
              <p className="text-sm text-purple-700">No relevant matches found.</p>
            ) : (
              <div className="space-y-3">
                {searchResults.map((r, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 border border-purple-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[10px] ${CATEGORY_COLORS[r.category] || CATEGORY_COLORS.General}`}>{r.category}</Badge>
                    </div>
                    <p className="font-medium text-sm text-slate-900">{r.question}</p>
                    <p className="text-sm text-slate-600 mt-1">{r.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Entries Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No knowledge base entries found.</p>
            <Button className="mt-3" onClick={() => setShowForm(true)}>Add Your First Entry</Button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map(entry => (
              <KBEntryCard
                key={entry.id}
                entry={entry}
                onEdit={(e) => { setEditingEntry(e); setShowForm(true); }}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
